import type { PaymentMode } from '@repo/desktop-bridge';
import { Button } from '@repo/ui/components/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { useFormContext } from 'react-hook-form';

import { formatPaise } from '@/lib/money';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { tenderedPaiseFrom, tenderKeys } from './cash-given';

/**
 * The field, the keys and the change.
 *
 * **Only on cash, and only once there is something to pay.** UPI and card hand
 * nothing back, and change from an empty sale is not a question — so it is not
 * drawn, rather than drawn disabled. Each screen clears the typed amount when
 * the mode leaves cash, for the reason its mode switch gives.
 *
 * What it is for is the end of a long day. A cashier subtracting ₹650 from
 * ₹1000 in their head four hundred times gets it right nearly every time, and
 * the drawer is short by the ones they did not. The keys are the notes somebody
 * plausibly hands over for *this* total, so the ordinary case is one tap and no
 * typing.
 */
export function CashGiven({ totalPaise, disabled }: { totalPaise: number; disabled: boolean }) {
  const { control, watch } = useFormContext<{ paymentMode?: PaymentMode; cashTendered?: string }>();
  const paymentMode = watch('paymentMode');
  const tenderedPaise = tenderedPaiseFrom(watch('cashTendered'));
  const tenders = tenderKeys(totalPaise);

  if (paymentMode !== 'CASH' || tenders.length === 0) return null;

  /**
   * What goes back across the counter — only once there is enough on the table
   * to give change from. Below the total is somebody still typing rather than a
   * customer who has underpaid, and telling them they are short by ₹649 on the
   * first keystroke is worse than saying nothing.
   */
  const changePaise =
    tenderedPaise !== null && tenderedPaise >= totalPaise ? tenderedPaise - totalPaise : null;

  return (
    <FormField
      control={control}
      name="cashTendered"
      render={({ field }) => (
        // **`min-w-[26rem]`, which is what this block's own contents measure**,
        // and `min-w-56` is what it had. The keys are 286px and cannot shrink;
        // the input is `flex-1 min-w-0` and therefore can, all the way down. So
        // a floor below the keys' own width is not a floor at all: at 1280x800
        // the row fitted inside 832px without wrapping, the block was handed
        // 314px, and the field a cashier types an amount into came out **20px
        // wide** with the three keys beside it intact. Stating the real minimum
        // is what makes the row wrap instead, which is the outcome that keeps
        // the field a field.
        <FormItem className="min-w-[26rem] flex-1">
          {/* **The answer sits on the question's own line**, and that is a
              room decision as much as a reading one. Stacked as its own row it
              cost the board above it four more rems it did not have. Beside the
              label it costs nothing and reads in the order it is thought: cash
              given, change back. */}
          <div className="flex items-baseline justify-between gap-3">
            <FormLabel className={fieldLabel}>Cash given</FormLabel>

            {changePaise !== null && (
              <span className="flex items-baseline gap-2">
                <span className={fieldLabel}>Change</span>
                {/* Deliberately smaller than the total in the column. The total
                    is what is owed and gets read aloud; this is an instruction
                    to a hand already in the drawer. */}
                <span className="font-mono text-2xl leading-none font-medium text-foreground numeric">
                  {formatPaise(changePaise)}
                </span>
              </span>
            )}
          </div>

          {/* **One row, and it is the width that makes it safe.** This foot is
              552px at the real window, the three keys are 286px of it and the
              input takes the rest — measured at 250px, which is a field
              somebody types an amount into.

              It does not survive being narrower, and that is worth writing
              down because it was tried: in a 312px column the keys still
              fitted, so nothing overflowed and nothing wrapped, and the whole
              shortfall came out of the `flex-1 min-w-0` input, which resolved
              to **18px**. Free space in a row is shared by flex-basis — the
              same arithmetic that took the board to 0px in #209, one field
              over. If this ever moves somewhere narrower, state the rows rather
              than trusting `flex-wrap` to notice. */}
          <div className="flex items-center gap-2">
            <FormControl
              render={
                <Input
                  className={`${fieldSurface} h-touch min-w-0 flex-1 font-mono text-lg numeric`}
                  placeholder="0.00"
                  autoComplete="off"
                  inputMode="decimal"
                  disabled={disabled}
                  {...field}
                />
              }
            />

            {tenders.map((amount) => (
              <Button
                key={amount}
                type="button"
                variant="outline"
                size="touch"
                className="shrink-0 px-3 font-mono text-sm numeric"
                disabled={disabled}
                onClick={() => {
                  // `rupeesFromPaise` would write `700.00`, which is correct and
                  // reads like a machine filled it in. These are whole notes by
                  // construction, so the paise are noise on a button somebody
                  // taps.
                  field.onChange(String(amount / 100));
                }}
              >
                {formatPaise(amount)}
              </Button>
            ))}
          </div>

          <FormMessage />
        </FormItem>
      )}
    />
  );
}
